import React, { useMemo, useState } from "react";
import { Slider } from "@/components/ui/slider";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Switch } from "@/components/ui/switch";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { Info, Activity, HeartPulse, BriefcaseMedical, Stethoscope, Plane, Heart } from "lucide-react";
import PolicySignDialog from "@/components/PolicySignDialog";

// ---------- Cenovne tabele (informativne, zdrav 35-letnik, monterstvo) ----------

// Težje bolezni — doba 20 let (€/mesec po zavarovalni vsoti)
const ILLNESS_PRICES = {
  10000: 7.67,
  20000: 13.34,
  30000: 19.01,
  40000: 24.67,
  50000: 30.34,
  75000: 44.5,
  100000: 58.67,
};

// Smrt — doba 20 let
const LIFE_PRICES = {
  10000: 2.85,
  20000: 3.84,
  30000: 4.84,
  50000: 6.83,
  100000: 12.73,
  150000: 19.1,
  200000: 24.54,
};

const ABSENCE_OPTIONS = [
  {
    id: "a200",
    label: "200 € / mesec — poškodba ali bolezen",
    price: ILLNESS_PRICES[10000] + 11.21, // 18.88
    note: "Vključuje težjo bolezen 10.000 €",
  },
  {
    id: "a400",
    label: "400 € / mesec — poškodba ali bolezen",
    price: ILLNESS_PRICES[10000] + 21.03, // 28.70
    note: "Vključuje težjo bolezen 10.000 €",
  },
  {
    id: "i500",
    label: "500 € / mesec — samo bolezen",
    price: 22.95,
    note: "Smiselno kombinirati z dnevnim nadomestilom pri nezgodi.",
  },
  {
    id: "i1000",
    label: "1000 € / mesec — samo bolezen",
    price: 45.9,
    note: "Smiselno kombinirati z dnevnim nadomestilom pri nezgodi.",
  },
  {
    id: "b500",
    label: "500 € / mesec — poškodba ali bolezen (brez težjih bolezni)",
    price: 42.23,
  },
  {
    id: "b1000",
    label: "1000 € / mesec — poškodba ali bolezen (brez težjih bolezni)",
    price: 84.46,
  },
];

const SPECIALIST_PRICE = 8.64;
const ABROAD_PRICE = 29.9;

const ACCIDENT_BASE_PER_EUR = 0.000125; // 10.000 € = 1,25 €/m
const ACCIDENT_DAILY_PER_EUR = 0.7666; // 1 €/dan = 0,7666 €/m

const ILLNESS_SUMS = Object.keys(ILLNESS_PRICES).map(Number);
const LIFE_SUMS = Object.keys(LIFE_PRICES).map(Number);

const fmt2 = (n) =>
  new Intl.NumberFormat("sl-SI", { style: "currency", currency: "EUR", maximumFractionDigits: 2 }).format(n);
const fmt0 = (n) =>
  new Intl.NumberFormat("sl-SI", { style: "currency", currency: "EUR", maximumFractionDigits: 0 }).format(n);

export default function PremiumCalculator() {
  // Nezgoda
  const [accident, setAccident] = useState(false);
  const [accidentSum, setAccidentSum] = useState(30000);
  const [accidentDaily, setAccidentDaily] = useState(15);

  // Težje bolezni
  const [illness, setIllness] = useState(false);
  const [illnessSum, setIllnessSum] = useState(10000);

  // Odsotnost z dela
  const [absence, setAbsence] = useState(false);
  const [absenceOption, setAbsenceOption] = useState("a200");

  // Specialisti
  const [specialist, setSpecialist] = useState(false);

  // Zdravljenje v tujini
  const [abroad, setAbroad] = useState(false);

  // Življenjsko (primer smrti)
  const [life, setLife] = useState(false);
  const [lifeSum, setLifeSum] = useState(50000);

  const lines = useMemo(() => {
    const out = [];
    if (accident) {
      const p = accidentSum * ACCIDENT_BASE_PER_EUR + accidentDaily * ACCIDENT_DAILY_PER_EUR;
      out.push({
        key: "accident",
        label: `Nezgoda — invalidnost ${fmt0(accidentSum)} + ${fmt2(accidentDaily)}/dan`,
        price: p,
      });
    }
    if (illness) {
      out.push({
        key: "illness",
        label: `Težje bolezni — ${fmt0(illnessSum)} (20 let)`,
        price: ILLNESS_PRICES[illnessSum],
      });
    }
    if (absence) {
      const opt = ABSENCE_OPTIONS.find((o) => o.id === absenceOption);
      out.push({ key: "absence", label: `Odsotnost z dela — ${opt.label}`, price: opt.price });
    }
    if (specialist) {
      out.push({ key: "specialist", label: "Hitri pregled pri specialistih", price: SPECIALIST_PRICE });
    }
    if (abroad) {
      out.push({ key: "abroad", label: "Zdravljenje v tujini", price: ABROAD_PRICE });
    }
    if (life) {
      out.push({
        key: "life",
        label: `Življenjsko (primer smrti) — ${fmt0(lifeSum)} (20 let)`,
        price: LIFE_PRICES[lifeSum],
      });
    }
    return out;
  }, [
    accident, accidentSum, accidentDaily,
    illness, illnessSum,
    absence, absenceOption,
    specialist, abroad,
    life, lifeSum,
  ]);

  const total = lines.reduce((s, l) => s + l.price, 0);

  const summary = lines.reduce((acc, l) => {
    acc[l.label] = `${fmt2(l.price)} / mesec`;
    return acc;
  }, { "Skupna mesečna premija": `${fmt2(total)} / mesec` });

  return (
    <div data-testid="premium-calculator" className="grid lg:grid-cols-5 gap-8">
      <div className="lg:col-span-3 space-y-4">
        <ToggleCard
          icon={<Activity size={18} />}
          title="Nezgoda"
          subtitle="Invalidnost + dnevno nadomestilo"
          enabled={accident}
          onChange={setAccident}
          testid="prem-accident"
        >
          <div className="space-y-5">
            <div>
              <div className="flex items-center justify-between mb-2">
                <Label className="text-sm font-semibold">Osnovna invalidnost</Label>
                <span className="font-display font-bold">{fmt0(accidentSum)}</span>
              </div>
              <Slider
                value={[accidentSum]}
                min={5000}
                max={200000}
                step={1000}
                onValueChange={(v) => setAccidentSum(v[0])}
                data-testid="prem-accident-sum"
              />
            </div>
            <div>
              <Label className="text-sm font-semibold" htmlFor="prem-daily">
                Višina dnevnega nadomestila (€)
              </Label>
              <Input
                id="prem-daily"
                data-testid="prem-accident-daily"
                type="number"
                min={0}
                step="0.5"
                value={accidentDaily}
                onChange={(e) => setAccidentDaily(Math.max(0, Number(e.target.value) || 0))}
                className="mt-2"
              />
            </div>
          </div>
        </ToggleCard>

        <ToggleCard
          icon={<HeartPulse size={18} />}
          title="Težje bolezni"
          subtitle="Doba zavarovanja 20 let"
          enabled={illness}
          onChange={setIllness}
          testid="prem-illness"
        >
          <Label className="text-sm font-semibold">Zavarovalna vsota</Label>
          <Select
            value={String(illnessSum)}
            onValueChange={(v) => setIllnessSum(Number(v))}
          >
            <SelectTrigger className="mt-2" data-testid="prem-illness-select">
              <SelectValue />
            </SelectTrigger>
            <SelectContent>
              {ILLNESS_SUMS.map((s) => (
                <SelectItem key={s} value={String(s)}>
                  {fmt0(s)} — {fmt2(ILLNESS_PRICES[s])} / mesec
                </SelectItem>
              ))}
            </SelectContent>
          </Select>
        </ToggleCard>

        <ToggleCard
          icon={<BriefcaseMedical size={18} />}
          title="Odsotnost z dela"
          subtitle="Bolniška zaradi poškodbe ali bolezni"
          enabled={absence}
          onChange={setAbsence}
          testid="prem-absence"
        >
          <Label className="text-sm font-semibold">Izberite paket</Label>
          <Select value={absenceOption} onValueChange={setAbsenceOption}>
            <SelectTrigger className="mt-2" data-testid="prem-absence-select">
              <SelectValue />
            </SelectTrigger>
            <SelectContent>
              {ABSENCE_OPTIONS.map((o) => (
                <SelectItem key={o.id} value={o.id}>
                  {o.label} — {fmt2(o.price)} / mesec
                </SelectItem>
              ))}
            </SelectContent>
          </Select>
          {ABSENCE_OPTIONS.find((o) => o.id === absenceOption)?.note && (
            <p className="text-xs text-[var(--vp-muted)] mt-2 flex items-start gap-1.5">
              <Info size={12} className="mt-0.5 flex-shrink-0" />
              {ABSENCE_OPTIONS.find((o) => o.id === absenceOption).note}
            </p>
          )}
        </ToggleCard>

        <ToggleCard
          icon={<Stethoscope size={18} />}
          title="Hitri pregled pri specialistih"
          subtitle="Dostop v parih dneh"
          enabled={specialist}
          onChange={setSpecialist}
          testid="prem-specialist"
          fixedPrice={SPECIALIST_PRICE}
        />

        <ToggleCard
          icon={<Plane size={18} />}
          title="Zdravljenje v tujini"
          subtitle="Bolezni in posegi v tujih klinikah"
          enabled={abroad}
          onChange={setAbroad}
          testid="prem-abroad"
          fixedPrice={ABROAD_PRICE}
        />

        <ToggleCard
          icon={<Heart size={18} />}
          title="Življenjsko zavarovanje"
          subtitle="Za primer smrti — doba 20 let"
          enabled={life}
          onChange={setLife}
          testid="prem-life"
        >
          <Label className="text-sm font-semibold">Zavarovalna vsota</Label>
          <Select
            value={String(lifeSum)}
            onValueChange={(v) => setLifeSum(Number(v))}
          >
            <SelectTrigger className="mt-2" data-testid="prem-life-select">
              <SelectValue />
            </SelectTrigger>
            <SelectContent>
              {LIFE_SUMS.map((s) => (
                <SelectItem key={s} value={String(s)}>
                  {fmt0(s)} — {fmt2(LIFE_PRICES[s])} / mesec
                </SelectItem>
              ))}
            </SelectContent>
          </Select>
        </ToggleCard>

        <div className="rounded-xl bg-[var(--vp-sand)] border border-[var(--vp-border)] p-4 flex gap-3">
          <Info className="text-[var(--vp-primary)] mt-0.5 flex-shrink-0" size={18} />
          <p className="text-xs text-[var(--vp-muted)] leading-relaxed">
            Prikazane cene so <strong>informativne</strong>. Cene so odvisne od starosti,
            življenjskega sloga, zdravstvenega stanja in vrste dela / poklica. Povprečna
            cena temelji na primeru <strong>zdrave osebe, stare 35 let, ki se ukvarja z
            monterstvom</strong>.
          </p>
        </div>
      </div>

      <div className="lg:col-span-2">
        <div
          className="rounded-2xl p-7 h-full flex flex-col sticky top-24"
          style={{ background: "var(--vp-primary)", color: "white" }}
        >
          <div className="text-sm opacity-80">Vaša mesečna premija</div>
          <div
            data-testid="prem-total"
            className="font-display font-bold text-4xl lg:text-5xl mt-2 tracking-tight"
          >
            {fmt2(total)}
            <span className="text-base font-medium opacity-80"> / mesec</span>
          </div>

          {lines.length === 0 ? (
            <div className="mt-6 text-sm opacity-80">
              Izberite zavarovanja, ki vas zanimajo. Mesečna premija se posodablja v živo.
            </div>
          ) : (
            <div className="mt-6 space-y-2.5 text-sm" data-testid="prem-breakdown">
              {lines.map((l) => (
                <div
                  key={l.key}
                  className="flex items-start justify-between gap-3 border-b border-white/15 pb-2"
                >
                  <span className="opacity-85 leading-snug">{l.label}</span>
                  <span className="font-semibold whitespace-nowrap">
                    {fmt2(l.price)}
                  </span>
                </div>
              ))}
            </div>
          )}

          <div className="mt-auto pt-7">
            <PolicySignDialog summary={summary} triggerLabel="Skleni si zavarovanje" />
          </div>
        </div>
      </div>
    </div>
  );
}

function ToggleCard({ icon, title, subtitle, enabled, onChange, testid, fixedPrice, children }) {
  return (
    <div
      data-testid={`${testid}-card`}
      className={`rounded-2xl border transition-colors ${
        enabled
          ? "border-[var(--vp-primary)] bg-[var(--vp-primary)]/5"
          : "border-[var(--vp-border)] bg-white"
      }`}
    >
      <div className="flex items-center gap-4 p-5">
        <div
          className="h-10 w-10 flex-shrink-0 rounded-xl flex items-center justify-center"
          style={{ background: "var(--vp-sand)", color: "var(--vp-primary)" }}
        >
          {icon}
        </div>
        <div className="flex-1 min-w-0">
          <div className="font-semibold">{title}</div>
          <div className="text-xs text-[var(--vp-muted)]">
            {subtitle}
            {fixedPrice != null && (
              <>
                {" · "}
                <span className="text-[var(--vp-primary)] font-semibold">
                  {fmt2(fixedPrice)} / mesec
                </span>
              </>
            )}
          </div>
        </div>
        <Switch
          data-testid={`${testid}-switch`}
          checked={enabled}
          onCheckedChange={onChange}
        />
      </div>
      {enabled && children && (
        <div className="border-t border-[var(--vp-border)] p-5">{children}</div>
      )}
    </div>
  );
}
